// hub → Products: tick products → "Run through Studio". Choose the steps and
// engines, see the cost and time first, then it runs on the server in the
// background; the results wait for review in Studio (Studio photos batches and
// Descriptions), never straight on the website.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Play, X } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { money, newKey, type EnginesInfo } from "./workbench";

export const STUDIO_JOB_MAX = 200;

interface JobEstimate { products: number; photos: number; descriptions: number; costUsd: number; minutes: number; tooMany: boolean; maxPhotos: number; blocked: string | null }

export function RunThroughStudio({ ids, onClose, onStarted }: { ids: string[]; onClose: () => void; onStarted: (msg: string) => void }) {
  const [info, setInfo] = useState<EnginesInfo | null>(null);
  const [photos, setPhotos] = useState(true);
  const [photoEngine, setPhotoEngine] = useState("default");
  const [backdrop, setBackdrop] = useState("");
  const [desc, setDesc] = useState(true);
  const [model, setModel] = useState("default");
  const [est, setEst] = useState<JobEstimate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const key = useRef(newKey());
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => { api<EnginesInfo>("/studio/workbench/engines").then(setInfo).catch(() => {}); panel.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const body = useMemo(() => ({
    requestKey: key.current, listingIds: ids,
    photos: photos ? { engine: photoEngine, ...(backdrop ? { backdrop } : {}) } : null,
    description: desc ? { engine: model } : null,
  }), [ids, photos, photoEngine, backdrop, desc, model]);
  useEffect(() => {
    if (!photos && !desc) { setEst(null); return; }
    let alive = true;
    setEst(null); setError(null);
    api<JobEstimate>("/studio/workbench/jobs/estimate", { method: "POST", body: JSON.stringify(body) })
      .then((r) => alive && setEst(r)).catch((e) => alive && setError(apiErrorMessage(e, "Could not work out the cost.")));
    return () => { alive = false; };
  }, [body, photos, desc]);
  const start = async () => {
    setBusy(true); setError(null);
    try {
      await api("/studio/workbench/jobs", { method: "POST", body: JSON.stringify(body) });
      onStarted(`Studio is running ${ids.length} product${ids.length === 1 ? "" : "s"} in the background. Review the results in Studio when they're done.`);
      onClose();
    } catch (e) { setError(apiErrorMessage(e, "Could not start the run.")); setBusy(false); }
  };
  const defaultPhoto = info ? `${info.engines.photo.find((e) => e.id === info.settings.photos.fallback.engine)?.label.split(" (")[0] ?? "Cutout"}, per department` : "per department";
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" aria-label="Run through Studio">
      <div className="absolute inset-0 bg-ink/30" onClick={onClose} aria-hidden />
      <div ref={panel} tabIndex={-1} className="relative flex w-full max-w-[560px] flex-col gap-4 rounded-t-lg bg-white p-6 shadow-md outline-none sm:rounded-lg">
        <header className="flex items-start gap-3">
          <div className="flex-1">
            <h2 className="text-[18px] font-semibold">Run {ids.length} product{ids.length === 1 ? "" : "s"} through Studio</h2>
            <p className="text-meta text-ink-muted">Runs on the server; you can close this page. Results wait for your review in Studio — nothing goes on the website until you approve it.</p>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="rounded p-2 hover:bg-paper"><X className="h-5 w-5" /></button>
        </header>
        <fieldset className="flex flex-col gap-2 rounded-lg border border-rule p-4">
          <label className="flex items-center gap-2 text-label font-semibold"><input type="checkbox" checked={photos} onChange={(e) => setPhotos(e.target.checked)} /> Studio photos</label>
          {photos && (
            <div className="flex flex-wrap gap-3 pl-6">
              <select value={photoEngine} onChange={(e) => setPhotoEngine(e.target.value)} className="h-9 rounded-md border border-rule bg-white px-2 text-meta" aria-label="Photo engine">
                <option value="default">Our defaults ({defaultPhoto})</option>
                {info?.engines.photo.map((c) => <option key={c.id} value={c.id} disabled={!c.ready}>{c.label}{c.ready ? "" : " — add a key"}</option>)}
              </select>
              <select value={backdrop} onChange={(e) => setBackdrop(e.target.value)} className="h-9 rounded-md border border-rule bg-white px-2 text-meta" aria-label="Backdrop">
                <option value="">Default backdrop per department</option>
                {info?.backdrops.map((b) => <option key={b.name} value={b.name}>{b.name} — {b.label}</option>)}
              </select>
            </div>
          )}
          <label className="mt-2 flex items-center gap-2 text-label font-semibold"><input type="checkbox" checked={desc} onChange={(e) => setDesc(e.target.checked)} /> Descriptions</label>
          {desc && (
            <div className="pl-6">
              <select value={model} onChange={(e) => setModel(e.target.value)} className="h-9 rounded-md border border-rule bg-white px-2 text-meta" aria-label="Description model">
                <option value="default">Our default ({info?.engines.text.find((e) => e.id === info.settings.description.engine)?.label ?? "Claude Opus 5.5"})</option>
                {info?.engines.text.map((c) => <option key={c.id} value={c.id} disabled={!c.ready}>{c.label}{c.ready ? "" : " — add a key"}</option>)}
              </select>
            </div>
          )}
        </fieldset>
        <div className="rounded-lg bg-paper p-4 text-label" aria-live="polite">
          {!photos && !desc ? "Choose photos, descriptions or both."
            : !est ? <span className="flex items-center gap-2 text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Working out the cost…</span>
            : (
              <>
                <p>About <b className="font-semibold">{est.photos.toLocaleString()} photos</b>{est.descriptions ? <> and <b className="font-semibold">{est.descriptions} descriptions</b></> : null} for {est.products} products.</p>
                <p className="text-meta text-ink-muted">About {money(est.costUsd)} and {est.minutes} min, more if the photo provider is throttling.</p>
                {est.tooMany && <p className="mt-1 text-meta text-risk">That is over {est.maxPhotos.toLocaleString()} photos for one run from the hub. Pick fewer products, or ask for a scripted run.</p>}
                {est.blocked && <p className="mt-1 text-meta text-risk">{est.blocked}</p>}
              </>
            )}
        </div>
        {error && <p className="text-meta text-risk" role="alert">{error}</p>}
        <footer className="flex flex-wrap items-center gap-3">
          <Link to="/studio?tab=engines" className="text-meta underline underline-offset-2">Change the default engines</Link>
          <Button variant="ghost" className="ml-auto" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !est || est.tooMany || !!est.blocked} onClick={() => void start()}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Play className="h-4 w-4" aria-hidden />} Start · about {money(est?.costUsd ?? 0)}
          </Button>
        </footer>
      </div>
    </div>
  );
}
